import * as React from "react";
import { Camera, CreditCard, FileCheck2, LockKeyhole, UploadCloud } from "lucide-react";
import type { DocumentScanDraft, PlanSource, UserPlan } from "@/types/tax";
import { createCheckoutSession, createCustomerPortalSession, scanDocument } from "@/api/client";
import { Alert } from "@/components/ui/Alert";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { PdfPreview } from "@/components/domain/PdfPreview";
import { extractPdfText, parseTaxSummaryText } from "@/lib/pdfParser";
import { isPaidPlan, planLabel, scanLimitForPlan, type SubscriptionCheckoutPlan } from "@/lib/plans";
import { currency } from "@/lib/utils";

type ScanKind = "income_summary" | "expense_bill";

interface PdfScanPanelProps {
  plan: UserPlan;
  planSource?: PlanSource;
  billingManagementAvailable?: boolean;
  scanQuotaLimit?: number;
  scanQuotaRemaining?: number;
  targetKind: ScanKind;
  onDraftIncome?: (draft: DocumentScanDraft) => void;
  onDraftExpense?: (draft: DocumentScanDraft) => void;
  onOpenSettings?: () => void;
}

export function PdfScanPanel({
  plan,
  billingManagementAvailable,
  scanQuotaLimit,
  scanQuotaRemaining,
  targetKind,
  onDraftIncome,
  onDraftExpense,
  onOpenSettings,
}: PdfScanPanelProps) {
  const [busy, setBusy] = React.useState(false);
  const [billingBusy, setBillingBusy] = React.useState<SubscriptionCheckoutPlan | "portal" | "">("");
  const [error, setError] = React.useState("");
  const [draft, setDraft] = React.useState<DocumentScanDraft | null>(null);

  async function startCheckout(checkoutPlan: SubscriptionCheckoutPlan) {
    setBillingBusy(checkoutPlan);
    setError("");
    try {
      const result = await createCheckoutSession(checkoutPlan);
      if (!result.ok || !result.data.url) {
        setError(result.ok ? "Stripe Checkout did not return a checkout link." : result.error);
        return;
      }
      window.location.assign(result.data.url);
    } finally {
      setBillingBusy("");
    }
  }

  async function openCustomerPortal() {
    setBillingBusy("portal");
    setError("");
    try {
      const result = await createCustomerPortalSession();
      if (!result.ok || !result.data.url) {
        setError(result.ok ? "Stripe did not return a billing portal link." : result.error);
        return;
      }
      window.location.assign(result.data.url);
    } finally {
      setBillingBusy("");
    }
  }

  async function onFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    setError("");
    setBusy(true);
    setDraft(null);
    try {
      if (!isSupportedScanFile(file)) {
        throw new Error("Upload a PDF, JPG, PNG, or WebP file.");
      }
      if (file.size > 12 * 1024 * 1024) {
        throw new Error("File must be under 12 MB.");
      }
      const localDraft = targetKind === "income_summary" && isPdfFile(file) ? await localIncomeDraft(file).catch(() => null) : null;
      const result = await scanDocument(file, targetKind, localDraft);
      if (!result.ok) throw new Error(result.error);
      const mergedDraft = cleanDraftWarnings(localDraft ? mergeIncomeDraft(result.data, localDraft) : result.data);
      setDraft(mergedDraft);
      if (targetKind === "income_summary") {
        onDraftIncome?.(mergedDraft);
      } else {
        onDraftExpense?.(mergedDraft);
      }
    } catch (scanError) {
      setError(scanError instanceof Error ? scanError.message : "Could not scan this file.");
    } finally {
      setBusy(false);
      event.target.value = "";
    }
  }

  const title = targetKind === "income_summary" ? "Scan income source" : "Scan expense bill";
  const description =
    targetKind === "income_summary"
      ? "Scan Uber, Didi, or other rideshare tax summaries from PDF or photos."
      : "Scan phone, electricity, fuel, or other supplier bills from PDF or photos.";
  const paidPlan = isPaidPlan(plan);
  const monthlyLimit = scanQuotaLimit || scanLimitForPlan(plan);
  const remaining = typeof scanQuotaRemaining === "number" ? scanQuotaRemaining : monthlyLimit;
  const quotaReached = paidPlan && monthlyLimit > 0 && remaining <= 0;
  const showQuotaStatus = monthlyLimit > 0 && (quotaReached || remaining < monthlyLimit);
  const canUpgradeToProPlus = plan === "pro";
  const canManageStripeUpgrade = canUpgradeToProPlus && Boolean(billingManagementAvailable);

  if (!paidPlan) {
    return (
      <Card>
        <CardHeader className="gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle>{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
          <Badge variant="payable">Pro</Badge>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex items-start gap-3 rounded-lg border border-border bg-surface p-4">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-white text-primary ring-1 ring-border">
              <LockKeyhole className="size-5" aria-hidden="true" />
            </div>
            <div>
              <p className="font-semibold text-ink">Document scanning is a Pro feature</p>
              <p className="mt-1 text-sm leading-6 text-muted">
                Free still supports manual GST entry. Pro includes 50 scans per month, and Pro Plus includes 150 scans per month.
              </p>
            </div>
          </div>
          {error ? <Alert variant="danger" title="Subscription">{error}</Alert> : null}
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button type="button" variant="credit" className="w-full sm:w-auto" disabled={billingBusy !== ""} onClick={() => startCheckout("pro")}>
              <CreditCard className="size-4" aria-hidden="true" />
              {billingBusy === "pro" ? "Opening..." : "Subscribe to Pro"}
            </Button>
            {onOpenSettings ? (
              <Button type="button" variant="secondary" className="w-full sm:w-auto" onClick={onOpenSettings}>
                View plans in Settings
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
        <div className="flex shrink-0 flex-col items-start gap-1 sm:items-end">
          <Badge variant="muted">{planLabel(plan)}</Badge>
          {monthlyLimit > 0 ? <span className="text-xs font-semibold text-muted">{monthlyLimit} scans/month</span> : null}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex min-h-36 cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-primary/40 bg-primary/5 px-4 py-6 text-center transition-colors hover:bg-primary/10 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-60">
            <UploadCloud className="size-8 text-primary" aria-hidden="true" />
            <span className="font-semibold text-ink">{busy ? "Scanning..." : "Choose file"}</span>
            <span className="max-w-sm text-sm leading-6 text-muted">PDF, JPG, PNG, or WebP.</span>
            <input type="file" accept={scanFileAccept} className="sr-only" onChange={onFileChange} disabled={busy || quotaReached} />
          </label>
          <label className="flex min-h-36 cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-credit/50 bg-credit/5 px-4 py-6 text-center transition-colors hover:bg-credit/10 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-60">
            <Camera className="size-8 text-credit" aria-hidden="true" />
            <span className="font-semibold text-ink">{busy ? "Scanning..." : "Take photo"}</span>
            <span className="max-w-sm text-sm leading-6 text-muted">Use the rear camera on mobile.</span>
            <input type="file" accept="image/*" capture="environment" className="sr-only" onChange={onFileChange} disabled={busy || quotaReached} />
          </label>
        </div>

        {showQuotaStatus ? (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-surface px-3 py-2">
            <p className="text-sm font-semibold text-ink">{quotaReached ? "Monthly scan limit reached" : `${remaining} scans left this month`}</p>
            <p className="text-xs font-semibold text-muted">{monthlyLimit}/month</p>
          </div>
        ) : null}
        {quotaReached ? (
          <Alert variant="warning" title="Scan limit reached">
            {canUpgradeToProPlus ? "Use the Pro Plus upgrade below for 150 scans per month." : "Your scan allowance resets next month."}
          </Alert>
        ) : null}
        {canUpgradeToProPlus ? (
          <div className="flex flex-col gap-3 rounded-lg border border-border bg-surface/60 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-ink">Need more scans?</p>
              <p className="mt-0.5 text-xs leading-5 text-muted">Pro Plus includes 150 scans/month. The higher limit applies after upgrade.</p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {onOpenSettings ? (
                <Button type="button" variant="ghost" size="sm" onClick={onOpenSettings}>
                  View plans
                </Button>
              ) : null}
              <Button
                type="button"
                variant={quotaReached ? "secondary" : "ghost"}
                size="sm"
                disabled={billingBusy !== ""}
                onClick={canManageStripeUpgrade ? openCustomerPortal : () => startCheckout("pro_plus")}
              >
                <CreditCard className="size-3.5" aria-hidden="true" />
                {billingBusy === "portal" || billingBusy === "pro_plus" ? "Opening..." : canManageStripeUpgrade ? "Manage" : "Upgrade"}
              </Button>
            </div>
          </div>
        ) : null}

        {error ? <Alert variant="danger" title="Scan failed">{error}</Alert> : null}

        {draft ? (
          <div className="rounded-lg border border-border bg-surface p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-start gap-3">
                <FileCheck2 className="mt-0.5 size-5 shrink-0 text-credit" aria-hidden="true" />
                <div className="min-w-0">
                  <p className="font-semibold text-ink">Draft populated in the form</p>
                  <p className="truncate text-sm text-muted">{draft.fileName}</p>
                </div>
              </div>
              <Badge variant={draft.confidence === "low" ? "payable" : "credit"}>{draft.confidence}</Badge>
            </div>

            <ScanSummary draft={draft} targetKind={targetKind} />

            {draft.warnings.map((warning) => (
              <Alert key={warning} variant="warning" className="mt-3">
                {warning}
              </Alert>
            ))}

            <PdfPreview documentId={draft.sourceDocumentId} fileName={draft.fileName} display="compact" className="mt-4" />
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

const scanFileAccept = "application/pdf,image/jpeg,image/png,image/webp,.pdf,.jpg,.jpeg,.png,.webp";

function isPdfFile(file: File) {
  return file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
}

function isSupportedScanFile(file: File) {
  const name = file.name.toLowerCase();
  return (
    isPdfFile(file) ||
    ["image/jpeg", "image/png", "image/webp"].includes(file.type) ||
    [".jpg", ".jpeg", ".png", ".webp"].some((extension) => name.endsWith(extension))
  );
}

async function localIncomeDraft(file: File): Promise<Partial<DocumentScanDraft>> {
  const rawText = await extractPdfText(file);
  const extraction = parseTaxSummaryText(rawText, file.name);
  if (extraction.confidence === "low" || extraction.grossFares <= 0) {
    return {};
  }

  return {
    documentKind: "income_summary",
    confidence: "medium",
    warnings: cleanScanWarnings(extraction.notes),
    income: {
      provider: extraction.provider,
      periodStart: extraction.periodStart,
      periodEnd: extraction.periodEnd,
      grossIncome: extraction.grossFares,
      platformFeesGross: extraction.platformFeesGross,
      platformFeesGst: extraction.platformFeesGst,
      tollsGross: extraction.tollsGross,
      tipsAndBonuses: extraction.tipsAndBonuses,
      notes: [
        ...extraction.notes,
        "Known tax summary fields were cross-checked with the local parser.",
      ].join("\n"),
    },
  };
}

function mergeIncomeDraft(serverDraft: DocumentScanDraft, localDraft: Partial<DocumentScanDraft>): DocumentScanDraft {
  if (!localDraft.income) return serverDraft;
  return {
    ...serverDraft,
    confidence: serverDraft.confidence === "low" ? (localDraft.confidence || "medium") : serverDraft.confidence,
    warnings: cleanScanWarnings([...(serverDraft.warnings || []), ...(localDraft.warnings || [])]),
    income: {
      ...(serverDraft.income || {}),
      ...localDraft.income,
    },
  };
}

function cleanDraftWarnings(draft: DocumentScanDraft): DocumentScanDraft {
  return {
    ...draft,
    warnings: cleanScanWarnings(draft.warnings || []),
  };
}

function cleanScanWarnings(warnings: string[]) {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const warning of warnings) {
    const clean = humanizeScanWarning(warning);
    const key = scanWarningKey(clean);
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(clean);
  }
  return result;
}

function humanizeScanWarning(warning: string) {
  if (/platformFeesGst|deductions?\s+gst|one-eleventh|explicit\s+gst\s+on\s+deductions/i.test(warning)) {
    return "GST on deductions was estimated as one-eleventh of total deductions because the PDF does not show a separate GST amount. Please review before saving.";
  }
  return warning
    .replace(/\bplatformFeesGst\b/g, "GST on deductions")
    .replace(/\bgrossIncome\b/g, "transportation income")
    .replace(/\bplatformFeesGross\b/g, "deductions");
}

function scanWarningKey(warning: string) {
  if (/GST on deductions was estimated/i.test(warning)) return "deductions-gst-estimated";
  return warning.toLowerCase().replace(/\s+/g, " ").trim();
}

function ScanSummary({ draft, targetKind }: { draft: DocumentScanDraft; targetKind: ScanKind }) {
  if (targetKind === "income_summary") {
    return (
      <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
        <SummaryItem label="Income" value={currency(Number(draft.income?.grossIncome || 0))} />
        <SummaryItem label="Period end" value={String(draft.income?.periodEnd || "Not found")} />
      </div>
    );
  }

  return (
    <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
      <SummaryItem label="Supplier" value={String(draft.expense?.supplier || "Not found")} />
      <SummaryItem label="Category" value={String(draft.expense?.category || "other").replace("_", " ")} />
      <SummaryItem label="Amount" value={currency(Number(draft.expense?.amount || 0))} />
      <SummaryItem label="Date" value={String(draft.expense?.date || "Not found")} />
    </div>
  );
}

function SummaryItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-white p-3">
      <p className="text-xs font-semibold text-muted">{label}</p>
      <p className="tabular mt-1 font-semibold text-ink">{value}</p>
    </div>
  );
}
